import type { CalendarBooking, CalendarPricePeriod, FreeSegment } from './types';

let _periods: CalendarPricePeriod[] = [];
let _bookings: CalendarBooking[] = [];

export function setPricingData(periods: CalendarPricePeriod[], bookings: CalendarBooking[]): void {
    _periods = periods;
    _bookings = bookings;
}

export function getDayPrice(unitId: number, dateYmd: string): number | null {
    for (const p of _periods) {
        if (p.unitId === unitId && dateYmd >= p.from && dateYmd <= p.to) {
            return p.perNight;
        }
    }
    return null;
}

export function isFreeDay(unitId: number, dateYmd: string, todayYmd: string): boolean {
    if (dateYmd < todayYmd) return false;
    for (const b of _bookings) {
        if (b.unitId === unitId && dateYmd >= b.from && dateYmd < b.to) return false;
    }
    return true;
}

export function getFreeSegments(unitId: number, windowFrom: string, windowTo: string): FreeSegment[] {
    const todayYmd = toYmd(new Date());
    const start = windowFrom < todayYmd ? todayYmd : windowFrom;
    if (start > windowTo) return [];

    const blocks = _bookings
        .filter(b => b.unitId === unitId)
        .map(b => ({ from: b.from, to: b.to }));

    const days = dateRange(start, windowTo);
    const segments: FreeSegment[] = [];
    let cur: FreeSegment & { key: string; _endDate: string } | null = null;

    for (const d of days) {
        const ds = toYmd(d);
        const blocked = blocks.some(b => ds >= b.from && ds < b.to);
        if (blocked) {
            if (cur) { segments.push(cur); cur = null; }
            continue;
        }
        const pInfo = getPriceInfo(unitId, ds);
        const key = pInfo ? `p:${pInfo.from}|${pInfo.to}|${pInfo.perNight}` : 'none';
        if (cur && cur.key === key && cur._endDate === prevDay(ds)) {
            cur.to = ds;
            cur.nights++;
            cur.total += pInfo?.perNight ?? 0;
            cur._endDate = ds;
        } else {
            if (cur) segments.push(cur);
            cur = {
                key,
                from: ds,
                to: ds,
                nights: 1,
                perNight: pInfo?.perNight ?? null,
                total: pInfo?.perNight ?? 0,
                label: pInfo?.label ?? null,
                startIdx: -1,
                endIdx: -1,
                _endDate: ds,
            };
        }
    }
    if (cur) segments.push(cur);
    return segments;
}

function getPriceInfo(unitId: number, dateYmd: string): CalendarPricePeriod | null {
    for (const p of _periods) {
        if (p.unitId === unitId && dateYmd >= p.from && dateYmd <= p.to) return p;
    }
    return null;
}

export function toYmd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
}

export function addDays(d: Date, n: number): Date {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
}

export function dateRange(fromYmd: string, toYmd: string): Date[] {
    const out: Date[] = [];
    const start = new Date(fromYmd + 'T00:00:00');
    const end = new Date(toYmd + 'T00:00:00');
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) out.push(new Date(d));
    return out;
}

export function daysBetween(fromYmd: string, toYmd: string): number {
    return Math.round((new Date(toYmd + 'T00:00:00').getTime() - new Date(fromYmd + 'T00:00:00').getTime()) / 86400000);
}

export function fmtPretty(ymdStr: string, monthNames: string[]): string {
    if (!ymdStr) return '—';
    const d = new Date(ymdStr + 'T00:00:00');
    return `${String(d.getDate()).padStart(2, '0')} ${monthNames[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
}

function prevDay(ymd: string): string {
    const d = new Date(ymd + 'T00:00:00');
    d.setDate(d.getDate() - 1);
    return toYmd(d);
}
